import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { UpdateService } from 'src/app/core/services/update.service';
import { NgIf } from '@angular/common';

/**
 * Right-side card shown on every deployed environment (dev / staging / prod) once
 * a new deploy is detected.
 *
 * Deliberately a SINGLE action — "Refresh now". There is no dismiss button: a
 * user who declines an update is exactly the user who ends up on a stale bundle,
 * and the card costs them nothing while it sits there. It is non-modal, takes no
 * focus, and its z-index is below the ngx-bootstrap modal layer, so it can never
 * cover or block anything the user is working on.
 *
 * This is the ACTIVE half of the update flow. The passive half —
 * UpdateNavigationService — applies the same pending update on the next
 * page-to-page navigation, so most users never need to click this at all.
 */
@Component({
    selector: 'app-update-banner',
    templateUrl: './update-banner.component.html',
    styleUrls: ['./update-banner.component.css'],
    imports: [NgIf],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateBannerComponent implements OnInit, OnDestroy {

  /** True once a new deploy has been detected. Never cleared by the user. */
  showCard = false;
  /** Latches on click so the button cannot be fired twice while the page unloads. */
  refreshing = false;

  private subscription?: Subscription;

  constructor(
    private updateService: UpdateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscription = this.updateService.updateAvailable$.subscribe((available) => {
      // Only ever turned ON here. UpdateService clears updateAvailable$ when the
      // update is activated, but by then the document is already being replaced.
      if (available && !this.showCard) {
        this.showCard = true;
        this.cdr.markForCheck();
      }
    });
  }

  refreshNow(): void {
    if (this.refreshing) { return; }
    this.refreshing = true;
    this.cdr.markForCheck();
    void this.updateService.activateUpdateAndReload();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
